import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchInboxApprovals, fetchSentApprovals } from '../../api/approval';
import { MENU_APPROVAL } from '../layout/Header';
import { useCompanyStore } from '../../store/companyStore';
import {
  ApprovalRequest,
  ApprovalStep,
  approvalStatusLabel,
  approvalStepTypeLabel,
} from '../../types/approval';
import { subscribeApprovalInboxChanged } from '../../utils/approvalInboxBadge';
import './ListWidgets.css';

const LIMIT = 8;

type ViewMode = 'inbox' | 'sent';

function formatDraftDate(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 16);
}

function pendingApproverLabel(item: ApprovalRequest) {
  if (item.status !== 'IN_PROGRESS') {
    return item.currentPendingNames || '-';
  }
  const pending = (item.steps || []).find((s: ApprovalStep) => s.status === 'PENDING');
  if (pending?.assigneeName) return pending.assigneeName;
  if (item.currentPendingNames) return item.currentPendingNames;
  if (pending) return approvalStepTypeLabel(pending.stepType);
  return '-';
}

function progressStatus(item: ApprovalRequest) {
  if (item.myPendingStepId && item.status === 'IN_PROGRESS') {
    return { label: '대기', className: 'is-wait' };
  }
  switch (item.status) {
    case 'IN_PROGRESS':
      return { label: approvalStatusLabel(item.status), className: 'is-progress' };
    case 'COMPLETED':
      return { label: approvalStatusLabel(item.status), className: 'is-done' };
    case 'REJECTED':
      return { label: approvalStatusLabel(item.status), className: 'is-reject' };
    case 'CANCELLED':
      return { label: approvalStatusLabel(item.status), className: 'is-cancel' };
    default:
      return { label: approvalStatusLabel(item.status), className: '' };
  }
}

function openDetailWindow(item: ApprovalRequest) {
  const url = `${window.location.origin}/approvals/${item.requestId}`;
  window.open(url, `approval-${item.requestId}`, 'noopener,width=1280,height=860');
}

function ApprovalWidget() {
  const navigate = useNavigate();
  const companyId = useCompanyStore((s) => s.companyId);
  const [mode, setMode] = useState<ViewMode>('inbox');
  const [rows, setRows] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result =
        mode === 'inbox'
          ? await fetchInboxApprovals({ page: 0, size: LIMIT })
          : await fetchSentApprovals({ page: 0, size: LIMIT });
      setRows(result.content || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    void load();
  }, [companyId, load]);

  useEffect(() => {
    return subscribeApprovalInboxChanged(() => {
      void load();
    });
  }, [load]);

  const openApproval = (tab: 'inbox' | 'sent' | 'all' = mode) => {
    navigate('/app', { state: { activeMenu: MENU_APPROVAL, approvalTab: tab } });
  };

  return (
    <section className="ec-widget ec-list-widget">
      <div className="ec-widget__header">
        <div className="ec-list-widget__title">
          <strong>전자결재</strong>
          <button
            type="button"
            className={`ec-list-widget__link${mode === 'inbox' ? ' is-active' : ''}`}
            onClick={() => setMode('inbox')}
          >
            결재할 문서
          </button>
          <button
            type="button"
            className={`ec-list-widget__link${mode === 'sent' ? ' is-active' : ''}`}
            onClick={() => setMode('sent')}
          >
            내 기안문서
          </button>
        </div>
        <div className="ec-widget__actions">
          <button type="button" className="ec-tool-btn" title="새로고침" onClick={() => void load()}>
            ↻
          </button>
          <button
            type="button"
            className="ec-tool-btn"
            title="결재 목록 열기"
            onClick={() => openApproval(mode)}
          >
            ⋯
          </button>
        </div>
      </div>

      <div className="ec-table-wrap">
        <table className="ec-table">
          <thead>
            <tr>
              <th style={{ width: '22%' }}>기안일자</th>
              <th>제목</th>
              <th style={{ width: '12%' }}>진행상태</th>
              <th style={{ width: '12%' }}>기안자</th>
              <th style={{ width: '12%' }}>결재자</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>불러오는 중...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <button type="button" className="ec-table__link" onClick={() => openApproval(mode)}>
                    {mode === 'inbox'
                      ? '처리할 결재가 없습니다. 결재 메뉴 열기'
                      : '올린 결재가 없습니다. 결재 메뉴 열기'}
                  </button>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const status = progressStatus(row);
                return (
                  <tr key={row.requestId}>
                    <td>{formatDraftDate(row.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="ec-table__link"
                        onClick={() => openDetailWindow(row)}
                        title="결재 상세 열기"
                      >
                        {row.title}
                      </button>
                    </td>
                    <td>
                      <span className={`ec-table__status ${status.className}`.trim()}>
                        {status.label}
                      </span>
                    </td>
                    <td>{row.requesterName || '-'}</td>
                    <td>{pendingApproverLabel(row)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default ApprovalWidget;
